import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { landlordAPI } from '../../lib/api';
import { downloadBlob } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { FileText, Plus, Loader2, Archive, Trash2, AlertTriangle, Search, X } from 'lucide-react';
import { LeaseHistoryDialog } from '../../components/ui/lease-history-dialog';
import ForceActivateLeaseDialog from '../../components/landlord/ForceActivateLeaseDialog';
import { LeaseFormFields } from '../../components/landlord/leases/LeaseFormFields';
import { LeaseCard } from '../../components/landlord/leases/LeaseCard';

export default function LandlordLeases() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [leases, setLeases] = useState([]);
  const [units, setUnits] = useState([]);
  const [availableTenants, setAvailableTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedLeaseId, setSelectedLeaseId] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [renewDialogOpen, setRenewDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [editingLease, setEditingLease] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState(getEmptyFormData());
  
  // Bulk delete state
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleteType, setBulkDeleteType] = useState(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Force activate state
  const [forceActivateDialogOpen, setForceActivateDialogOpen] = useState(false);
  const [forceActivateLeaseId, setForceActivateLeaseId] = useState(null);
  const [forceActivateReason, setForceActivateReason] = useState('');
  const [forceActivating, setForceActivating] = useState(false);

  function getEmptyFormData() {
    return {
      unit_id: '',
      tenant_id: '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      base_rent: '',
      deposit_amount: '',
      // Additional fees
      internet: '',
      water: '',
      electricity: '',
      appliances: '',
      laundry: '',
      other1_name: '',
      other1_amount: '',
      other2_name: '',
      other2_amount: '',
      // Lease terms
      house_rules: '',
      payment_terms: '',
      terms_of_service: '',
    };
  }

  useEffect(() => {
    loadData();
  }, []);
  
  // Handle query params for opening create dialog with pre-selected tenant
  useEffect(() => {
    const shouldOpenNewLease = searchParams.get('newLease') === 'true';
    const preSelectedTenantId = searchParams.get('tenant');
    
    if (shouldOpenNewLease && !loading && availableTenants.length >= 0) {
      // Open the create lease dialog
      handleOpenDialog(preSelectedTenantId);
      // Clear the search params to prevent re-opening on refresh
      setSearchParams({});
    }
  }, [loading, searchParams, availableTenants]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional mount-only load

  const loadData = async () => {
    try {
      const [leasesRes, unitsRes, tenantsRes] = await Promise.all([
        landlordAPI.getLeases(),
        landlordAPI.getUnits(),
        landlordAPI.getAvailableTenants(),
      ]);
      setLeases(leasesRes.data);
      setUnits(unitsRes.data.filter(u => u.status !== 'occupied'));
      setAvailableTenants(tenantsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const filteredLeases = (activeTab === 'all' 
    ? leases.filter(l => l.status !== 'archived')
    : activeTab === 'archived'
    ? leases.filter(l => l.status === 'archived')
    : leases.filter(l => l.status === activeTab)
  ).filter(l => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const matchTenant = (l.tenant?.name || '').toLowerCase().includes(q);
    const matchEmail = (l.tenant?.email || '').toLowerCase().includes(q);
    const matchUnit = (l.unit?.unit_number || '').toLowerCase().includes(q);
    const matchProperty = (l.property?.name || '').toLowerCase().includes(q);
    const matchStatus = (l.status || '').toLowerCase().includes(q);
    return matchTenant || matchEmail || matchUnit || matchProperty || matchStatus;
  });

  const handleOpenDialog = async (preSelectedTenantId = null) => {
    // Start with empty form
    const emptyForm = getEmptyFormData();
    
    // If a tenant ID is provided, pre-select them (only if actually available for a new lease)
    if (preSelectedTenantId) {
      if (availableTenants.some(t => t.user_id === preSelectedTenantId)) {
        emptyForm.tenant_id = preSelectedTenantId;
      } else {
        toast.warning('This tenant already has an active or pending lease — select another tenant.');
      }
    }
    
    // Load landlord's saved lease terms
    try {
      const response = await landlordAPI.getLeaseTerms();
      emptyForm.house_rules = response.data.house_rules || '';
      emptyForm.payment_terms = response.data.payment_terms || '';
      emptyForm.terms_of_service = response.data.terms_of_service || '';
    } catch (error) {
      console.error('Error loading lease terms:', error);
    }
    
    setFormData(emptyForm);
    setDialogOpen(true);
  };

  const handleOpenEditDialog = (lease) => {
    if (lease.is_locked || lease.tenant_acknowledged) {
      toast.error('This lease is locked. Use "Issue New Lease" to make changes.');
      return;
    }
    setEditingLease(lease);
    setFormData({
      unit_id: lease.unit_id,
      tenant_id: lease.tenant_id,
      start_date: lease.start_date,
      end_date: lease.end_date,
      base_rent: lease.base_rent?.toString() || '',
      deposit_amount: lease.deposit_amount?.toString() || '',
      internet: lease.additional_fees?.internet?.toString() || '',
      water: lease.additional_fees?.water?.toString() || '',
      electricity: lease.additional_fees?.electricity?.toString() || '',
      appliances: lease.additional_fees?.appliances?.toString() || '',
      laundry: lease.additional_fees?.laundry?.toString() || '',
      other1_name: lease.additional_fees?.other1_name || '',
      other1_amount: lease.additional_fees?.other1_amount?.toString() || '',
      other2_name: lease.additional_fees?.other2_name || '',
      other2_amount: lease.additional_fees?.other2_amount?.toString() || '',
      house_rules: lease.lease_terms?.house_rules || '',
      payment_terms: lease.lease_terms?.payment_terms || '',
      terms_of_service: lease.lease_terms?.terms_of_service || '',
    });
    setEditDialogOpen(true);
  };

  const handleOpenRenewDialog = async (lease) => {
    setEditingLease(lease);
    
    // Load landlord's current saved lease terms (not old lease terms)
    let leaseTerms = {
      house_rules: '',
      payment_terms: '',
      terms_of_service: ''
    };
    try {
      const response = await landlordAPI.getLeaseTerms();
      leaseTerms = response.data;
    } catch (error) {
      console.error('Error loading lease terms:', error);
    }
    
    setFormData({
      unit_id: lease.unit_id,
      tenant_id: lease.tenant_id,
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      base_rent: lease.base_rent?.toString() || '',
      deposit_amount: lease.deposit_amount?.toString() || '',
      internet: lease.additional_fees?.internet?.toString() || '',
      water: lease.additional_fees?.water?.toString() || '',
      electricity: lease.additional_fees?.electricity?.toString() || '',
      appliances: lease.additional_fees?.appliances?.toString() || '',
      laundry: lease.additional_fees?.laundry?.toString() || '',
      other1_name: lease.additional_fees?.other1_name || '',
      other1_amount: lease.additional_fees?.other1_amount?.toString() || '',
      other2_name: lease.additional_fees?.other2_name || '',
      other2_amount: lease.additional_fees?.other2_amount?.toString() || '',
      house_rules: leaseTerms.house_rules || '',
      payment_terms: leaseTerms.payment_terms || '',
      terms_of_service: leaseTerms.terms_of_service || '',
    });
    setRenewDialogOpen(true);
  };

  const handleUnitChange = (unitId) => {
    const unit = units.find(u => u.unit_id === unitId);
    setFormData({
      ...formData,
      unit_id: unitId,
      base_rent: unit?.monthly_rent?.toString() || '',
      deposit_amount: unit?.monthly_rent?.toString() || '',
    });
  };

  const buildLeasePayload = () => {
    return {
      unit_id: formData.unit_id,
      tenant_id: formData.tenant_id,
      start_date: formData.start_date,
      end_date: formData.end_date,
      base_rent: parseFloat(formData.base_rent),
      deposit_amount: parseFloat(formData.deposit_amount) || 0,
      additional_fees: {
        internet: parseFloat(formData.internet) || 0,
        water: parseFloat(formData.water) || 0,
        electricity: parseFloat(formData.electricity) || 0,
        appliances: parseFloat(formData.appliances) || 0,
        laundry: parseFloat(formData.laundry) || 0,
        other1_name: formData.other1_name || null,
        other1_amount: parseFloat(formData.other1_amount) || 0,
        other2_name: formData.other2_name || null,
        other2_amount: parseFloat(formData.other2_amount) || 0,
      },
      lease_terms: {
        house_rules: formData.house_rules || null,
        payment_terms: formData.payment_terms || null,
        terms_of_service: formData.terms_of_service || null,
      },
    };
  };

  const handleSave = async () => {
    if (!formData.unit_id || !formData.tenant_id || !formData.start_date || !formData.end_date || !formData.base_rent) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      await landlordAPI.createLease(buildLeasePayload());
      toast.success('Lease created successfully');
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create lease');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingLease) return;

    setSaving(true);
    try {
      await landlordAPI.updateLease(editingLease.lease_id, {
        start_date: formData.start_date,
        end_date: formData.end_date,
        base_rent: parseFloat(formData.base_rent),
        deposit_amount: parseFloat(formData.deposit_amount) || 0,
        additional_fees: {
          internet: parseFloat(formData.internet) || 0,
          water: parseFloat(formData.water) || 0,
          electricity: parseFloat(formData.electricity) || 0,
          appliances: parseFloat(formData.appliances) || 0,
          laundry: parseFloat(formData.laundry) || 0,
          other1_name: formData.other1_name || null,
          other1_amount: parseFloat(formData.other1_amount) || 0,
          other2_name: formData.other2_name || null,
          other2_amount: parseFloat(formData.other2_amount) || 0,
        },
        lease_terms: {
          house_rules: formData.house_rules || null,
          payment_terms: formData.payment_terms || null,
          terms_of_service: formData.terms_of_service || null,
        },
      });
      toast.success('Lease updated successfully');
      setEditDialogOpen(false);
      setEditingLease(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update lease');
    } finally {
      setSaving(false);
    }
  };

  const handleArchiveAndRenew = async () => {
    if (!editingLease) return;

    setSaving(true);
    try {
      await landlordAPI.archiveAndRenew(editingLease.lease_id, buildLeasePayload());
      toast.success('Old lease archived, new lease created for tenant acknowledgement');
      setRenewDialogOpen(false);
      setEditingLease(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create new lease');
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (leaseId) => {
    try {
      await landlordAPI.activateLease(leaseId);
      toast.success('Lease activated successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate lease');
    }
  };

  const openForceActivateDialog = (leaseId) => {
    setForceActivateLeaseId(leaseId);
    setForceActivateReason('');
    setForceActivateDialogOpen(true);
  };

  const handleForceActivate = async () => {
    if (!forceActivateReason.trim()) {
      toast.error('Please provide a reason for force activation');
      return;
    }
    setForceActivating(true);
    try {
      await landlordAPI.forceActivateLease(forceActivateLeaseId, forceActivateReason.trim());
      toast.success('Lease force-activated successfully');
      setForceActivateDialogOpen(false);
      setForceActivateLeaseId(null);
      setForceActivateReason('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to force-activate lease');
    } finally {
      setForceActivating(false);
    }
  };

  const handleTerminate = async (leaseId) => {
    if (!window.confirm('Are you sure you want to terminate this lease?')) return;
    try {
      await landlordAPI.terminateLease(leaseId);
      toast.success('Lease terminated');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to terminate lease');
    }
  };

  const handleDeleteLease = async (leaseId) => {
    setDeleting(leaseId);
    try {
      await landlordAPI.deleteLease(leaseId);
      toast.success('Lease deleted successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete lease');
    } finally {
      setDeleting(null);
    }
  };

  const handleBulkDelete = async () => {
    if (!bulkDeleteType) return;
    
    setBulkDeleting(true);
    try {
      const response = await landlordAPI.bulkDeleteLeases(bulkDeleteType, null);
      const { deleted_count, skipped_count, skipped_reasons } = response.data;
      
      if (deleted_count > 0) {
        toast.success(`Successfully deleted ${deleted_count} lease(s)`);
      }
      
      if (skipped_count > 0) {
        toast.warning(`${skipped_count} lease(s) skipped (have payment history)`);
      }
      
      if (deleted_count === 0 && skipped_count === 0) {
        toast.info('No leases found to delete');
      }
      
      loadData();
      setBulkDeleteDialogOpen(false);
      setBulkDeleteType(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk delete leases');
    } finally {
      setBulkDeleting(false);
    }
  };

  const getLeaseCountByStatus = (status) => {
    if (status === 'all') return leases.length;
    return leases.filter(l => l.status === status).length;
  };

  const handleDownloadPdf = async (leaseId) => {
    try {
      const response = await landlordAPI.exportLeasePdf(leaseId);
      downloadBlob(response.data, `lease_agreement_${leaseId}.pdf`);
      toast.success('Lease PDF downloaded and shared with tenant');
    } catch (error) {
      toast.error('Failed to download lease PDF');
    }
  };

  const handleViewHistory = (leaseId) => {
    setSelectedLeaseId(leaseId);
    setHistoryDialogOpen(true);
  };

  const leaseFormProps = {
    formData, setFormData, units, availableTenants, onUnitChange: handleUnitChange
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="landlord-leases-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold">Lease Agreements</h1>
          <p className="text-muted-foreground mt-1">Manage tenant leases</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search leases..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-8"
              data-testid="lease-search-input"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {/* Bulk Delete Dropdown */}
          {leases.length > 0 && (
            <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
              <Select
                value=""
                onValueChange={(value) => {
                  setBulkDeleteType(value);
                  setBulkDeleteDialogOpen(true);
                }}
              >
                <SelectTrigger className="w-[160px]" data-testid="bulk-delete-trigger">
                  <div className="flex items-center gap-2 text-red-600">
                    <Trash2 className="w-4 h-4" />
                    <span>Bulk Delete</span>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="text-red-600">
                    <div className="flex items-center gap-2">
                      <Trash2 className="w-4 h-4" />
                      All Leases ({leases.filter(l => l.status !== 'archived').length})
                    </div>
                  </SelectItem>
                  <SelectItem value="pending" className="text-amber-600">
                    <div className="flex items-center gap-2">
                      <Trash2 className="w-4 h-4" />
                      Pending ({leases.filter(l => l.status === 'pending').length})
                    </div>
                  </SelectItem>
                  <SelectItem value="terminated" className="text-gray-600">
                    <div className="flex items-center gap-2">
                      <Trash2 className="w-4 h-4" />
                      Terminated ({leases.filter(l => l.status === 'terminated').length})
                    </div>
                  </SelectItem>
                  <SelectItem value="expired" className="text-orange-600">
                    <div className="flex items-center gap-2">
                      <Trash2 className="w-4 h-4" />
                      Expired ({leases.filter(l => l.status === 'expired').length})
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                    <AlertTriangle className="w-5 h-5" />
                    Confirm Bulk Delete
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    You are about to delete <strong className="text-red-600">
                      {bulkDeleteType === 'all' 
                        ? `all ${leases.filter(l => l.status !== 'archived').length}` 
                        : `${leases.filter(l => l.status === bulkDeleteType).length} ${bulkDeleteType}`}
                    </strong> lease(s).
                  </AlertDialogDescription>
                  <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-sm">
                    <p className="font-medium text-amber-800 dark:text-amber-300">⚠️ Note:</p>
                    <ul className="list-disc list-inside text-amber-700 dark:text-amber-400 mt-1">
                      <li>Active leases with payment history will be skipped</li>
                      <li>This action cannot be undone</li>
                      <li>Units will be released back to vacant status</li>
                    </ul>
                  </div>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel onClick={() => setBulkDeleteType(null)}>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleBulkDelete}
                    disabled={bulkDeleting}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {bulkDeleting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete {bulkDeleteType === 'all' ? 'All' : bulkDeleteType?.charAt(0).toUpperCase() + bulkDeleteType?.slice(1)}
                      </>
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          
          <Button onClick={handleOpenDialog} className="bg-[#C85A42] hover:bg-[#B04A35]" disabled={units.length === 0 || availableTenants.length === 0} data-testid="create-lease-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Lease
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All ({leases.filter(l => l.status !== 'archived').length})</TabsTrigger>
          <TabsTrigger value="pending">Pending ({leases.filter(l => l.status === 'pending').length})</TabsTrigger>
          <TabsTrigger value="active">Active ({leases.filter(l => l.status === 'active').length})</TabsTrigger>
          <TabsTrigger value="terminated">Terminated ({leases.filter(l => l.status === 'terminated').length})</TabsTrigger>
          <TabsTrigger value="archived">
            <Archive className="w-4 h-4 mr-1" />
            Archived ({leases.filter(l => l.status === 'archived').length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {filteredLeases.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="font-semibold text-lg mb-2">No leases found</h3>
                <p className="text-muted-foreground">
                  {units.length === 0 ? 'Add available units first' : availableTenants.length === 0 ? 'No available tenants' : 'Create your first lease agreement'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredLeases.map((lease, index) => (
                <LeaseCard
                  key={lease.lease_id}
                  lease={lease}
                  index={index}
                  deleting={deleting}
                  onEdit={handleOpenEditDialog}
                  onActivate={handleActivate}
                  onForceActivate={openForceActivateDialog}
                  onRenew={handleOpenRenewDialog}
                  onHistory={handleViewHistory}
                  onDownloadPdf={handleDownloadPdf}
                  onTerminate={handleTerminate}
                  onDelete={handleDeleteLease}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Lease History Dialog */}
      <LeaseHistoryDialog
        open={historyDialogOpen}
        onOpenChange={setHistoryDialogOpen}
        leaseId={selectedLeaseId}
      />

      {/* Create Lease Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Create New Lease Agreement</DialogTitle>
            <DialogDescription>
              Fill in the lease details. The tenant must acknowledge before it becomes active.
            </DialogDescription>
          </DialogHeader>
          <LeaseFormFields {...leaseFormProps} />
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#C85A42] hover:bg-[#B04A35]" data-testid="save-lease-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Lease
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Lease Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit Lease Agreement</DialogTitle>
            <DialogDescription>
              You can edit this lease until the tenant acknowledges it. After acknowledgement, the lease will be locked.
            </DialogDescription>
          </DialogHeader>
          <LeaseFormFields {...leaseFormProps} isEdit />
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setEditDialogOpen(false); setEditingLease(null); }}>Cancel</Button>
            <Button onClick={handleUpdate} disabled={saving} className="bg-[#C85A42] hover:bg-[#B04A35]" data-testid="update-lease-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Update Lease
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive & Renew Dialog */}
      <Dialog open={renewDialogOpen} onOpenChange={setRenewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="w-5 h-5" />
              Issue New Lease Agreement
            </DialogTitle>
            <DialogDescription>
              This will archive the current lease and create a new one with updated terms. The tenant will need to acknowledge the new lease.
            </DialogDescription>
          </DialogHeader>
          <LeaseFormFields {...leaseFormProps} isRenew />
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setRenewDialogOpen(false); setEditingLease(null); }}>Cancel</Button>
            <Button onClick={handleArchiveAndRenew} disabled={saving} className="bg-[#C85A42] hover:bg-[#B04A35]" data-testid="renew-lease-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Archive Old & Create New Lease
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Force Activate Lease Dialog */}
      <ForceActivateLeaseDialog
        open={forceActivateDialogOpen}
        onOpenChange={(open) => {
          setForceActivateDialogOpen(open);
          if (!open) {
            setForceActivateLeaseId(null);
            setForceActivateReason('');
          }
        }}
        reason={forceActivateReason}
        onReasonChange={setForceActivateReason}
        submitting={forceActivating}
        onConfirm={handleForceActivate}
      />
    </div>
  );
}
